import { Button, Input, Space } from "antd";
import { useEffect, useState } from "react";
import type { File as FileEntity } from "@zhihaoscm/entity";
import { fileService } from "@/api/services/fileService";
import { Dialog as ZhDialog } from "zhihao-ui-react";

export interface WeighingListEntryLine {
    goodsCode: number;
    goodsName: string;
    unitPrice: number;
    settlementRate: number;
    plannedTonnage: number;
    grossWeight: string;
}

export interface WeighingListEntryFormValue {
    loadingEndTime: string;
    attachmentFileIds: number[];
    lines: WeighingListEntryLine[];
}

export interface WeighingListEntryDialogProps {
    open: boolean;
    mode: "create" | "edit";
    waybillNo: string;
    shipName: string;
    loadingEndTime: string;
    attachmentFileIds: number[];
    lines: WeighingListEntryLine[];
    onCancel: () => void;
    onSubmit: (value: WeighingListEntryFormValue) => void;
}

/** Vue 过磅单弹窗的 React 版本，保留船舶、物料、附件和底部确认区。 */
export function WeighingListEntryDialog({
    open,
    mode,
    waybillNo,
    shipName,
    loadingEndTime,
    attachmentFileIds,
    lines: initialLines,
    onCancel,
    onSubmit
}: WeighingListEntryDialogProps) {
    const [endTime, setEndTime] = useState(loadingEndTime);
    const [lines, setLines] = useState<WeighingListEntryLine[]>(initialLines);
    const [attachmentFiles, setAttachmentFiles] = useState<FileEntity[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setEndTime(loadingEndTime);
        setLines(initialLines);
        setError(null);
    }, [initialLines, loadingEndTime, open]);

    /** 编辑已有过磅单时回显文件名，保持旧版上传控件的已上传文件列表。 */
    useEffect(() => {
        if (!open || !attachmentFileIds.length) {
            setAttachmentFiles([]);
            return;
        }
        let active = true;
        void fileService
            .getFileByIds(attachmentFileIds)
            .then(files => {
                if (active) setAttachmentFiles(files);
            })
            .catch(() => {
                if (active) setAttachmentFiles([]);
            });
        return () => {
            active = false;
        };
    }, [attachmentFileIds, open]);

    const submit = () => {
        const invalid = lines.some(line => {
            const value = Number(line.grossWeight.replace(/,/g, ""));
            return !Number.isFinite(value) || value <= 0;
        });
        if (!endTime.trim()) {
            setError("请选择装船结束时间");
            return;
        }
        if (invalid) {
            setError("请输入正确的过磅吨数");
            return;
        }
        setError(null);
        onSubmit({ loadingEndTime: endTime.trim(), attachmentFileIds, lines });
    };

    return (
        <ZhDialog
            modalClass="zone-reservation-weigh-dialog"
            open={open}
            title={mode === "edit" ? "编辑过磅单" : "磅单录入"}
            width="920px"
            onCancel={onCancel}
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={submit}>
                        确认
                    </Button>
                </Space>
            }
        >
            <div className="zone-reservation-weigh-dialog__body">
                {error ? <div className="zone-reservation-weigh-dialog__error">{error}</div> : null}
                <section className="zone-reservation-weigh-dialog__section">
                    <div className="zone-reservation-weigh-dialog__section-title">船舶信息</div>
                    <div className="zone-reservation-weigh-dialog__ship-grid">
                        <div className="zone-reservation-weigh-dialog__field">
                            <span>船运单编号</span>
                            <strong>{waybillNo || "--"}</strong>
                        </div>
                        <div className="zone-reservation-weigh-dialog__field">
                            <span>船名</span>
                            <strong className={shipName ? "zone-reservation-weigh-dialog__link" : ""}>
                                {shipName || "--"}
                            </strong>
                        </div>
                        <label className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__time">
                            <span>* 装船结束时间</span>
                            <Input
                                aria-label="装船结束时间"
                                value={endTime}
                                onChange={event => setEndTime(event.target.value)}
                            />
                        </label>
                    </div>
                </section>

                <section className="zone-reservation-weigh-dialog__section">
                    <div className="zone-reservation-weigh-dialog__section-title">物料信息</div>
                    {lines.map((line, index) => (
                        <div className="zone-reservation-weigh-dialog__material-card" key={`${line.goodsCode}-${index}`}>
                            <div className="zone-reservation-weigh-dialog__material-grid">
                                <div className="zone-reservation-weigh-dialog__field">
                                    <span>物料名称</span>
                                    <strong>{line.goodsName || "--"}</strong>
                                </div>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>{mode === "edit" ? "* 单价" : "单价"}</span>
                                    <Input readOnly suffix="元" value={line.unitPrice ? String(line.unitPrice) : ""} />
                                </div>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>结算率</span>
                                    <strong>{line.settlementRate || "--"}</strong>
                                </div>
                                <label className="zone-reservation-weigh-dialog__field">
                                    <span>过磅吨数</span>
                                    <Input
                                        aria-label={`${line.goodsName}过磅吨数`}
                                        suffix="吨"
                                        value={line.grossWeight}
                                        onChange={event =>
                                            setLines(current =>
                                                current.map((item, itemIndex) =>
                                                    itemIndex === index ? { ...item, grossWeight: event.target.value } : item
                                                )
                                            )
                                        }
                                    />
                                </label>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>结算吨数</span>
                                    <strong>
                                        {line.grossWeight
                                            ? `${(Number(line.grossWeight) * (Number(line.settlementRate) || 0)).toLocaleString("zh-CN")} 吨`
                                            : "--"}
                                    </strong>
                                </div>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>结算金额</span>
                                    <strong>
                                        ¥
                                        {(
                                            (Number(line.grossWeight) || 0) *
                                            (Number(line.unitPrice) || 0) *
                                            (Number(line.settlementRate) || 0)
                                        ).toLocaleString("zh-CN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="zone-reservation-weigh-dialog__attachment">
                        <span>附件</span>
                        <div>
                            <div className="zone-reservation-weigh-dialog__upload-placeholder">点击或拖拽上传文件</div>
                            <small>支持 jpg、png、pdf，单个文件不超过10MB，最多上传5个</small>
                            {attachmentFiles.length ? (
                                <div className="zone-reservation-weigh-dialog__file-list">
                                    {attachmentFiles.map(file => (
                                        <div className="zone-reservation-weigh-dialog__file-item" key={file.id}>
                                            <span className="zone-reservation-weigh-dialog__file-name">
                                                <span aria-hidden="true">▧</span>
                                                {file.name}
                                            </span>
                                            <span aria-hidden="true">×</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </div>
        </ZhDialog>
    );
}

export default WeighingListEntryDialog;
